// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * Star Systems Data - Streamlined Milky Way
 * 
 * 15 essential systems for a balanced gameplay experience
 */

export const starSystems = [
  { 
    id: 0, 
    name: 'SOL (EARTH)', 
    type: 'planet',
    distance: 0,
    coordinates: { x: 45, y: 50, z: 0 }, // Visual center
    threat: 'SAFE', 
    resources: 'HOME',
    services: ['refuel', 'repairs', 'trade', 'weapons', 'upgrades', 'modules'],
    tradeType: 'default',
    primaryColor: '#4488ff',
    secondaryColor: '#6699ff',
    hasRings: false,
    description: 'Birthplace of humanity. Blue marble in the void. All journeys begin here.',
    aliens: ['vulcans', 'ferengi', 'andorians']
  },
  { 
    id: 2, 
    name: 'PROXIMA CENTAURI', 
    type: 'planet',
    distance: 4.24,
    coordinates: { x: 48, y: 52, z: 2 },
    threat: 'MINIMAL', 
    resources: 'MEDIUM',
    services: ['refuel', 'exploration', 'repairs', 'trade', 'weapons'],
    tradeType: 'agricultural',
    primaryColor: '#ffaa44',
    secondaryColor: '#ffcc66',
    hasRings: false,
    description: 'Closest neighbor to Sol. Red dwarf with tidally-locked rocky planet.',
    aliens: ['vulcans', 'andorians']
  },
  { 
    id: 3, 
    name: 'WOLF 359', 
    type: 'planet',
    distance: 7.86,
    coordinates: { x: 42, y: 48, z: -2 },
    threat: 'MODERATE', 
    resources: 'LOW',
    services: ['refuel', 'exploration', 'repairs', 'trade', 'weapons'],
    tradeType: 'default',
    primaryColor: '#cc8844',
    secondaryColor: '#ddaa66',
    hasRings: false,
    description: 'Dim red dwarf with barren worlds. Strategic location for deep space operations.',
    aliens: ['borg', 'greys']
  },
  { 
    id: 4, 
    name: 'GLIESE 667C', 
    type: 'planet',
    distance: 23.62,
    coordinates: { x: 60, y: 58, z: 5 },
    threat: 'HIGH', 
    resources: 'VERY HIGH',
    services: ['refuel', 'exploration', 'repairs', 'trade'],
    tradeType: 'mining',
    primaryColor: '#44ff88',
    secondaryColor: '#66ffaa',
    hasRings: true,
    description: 'Triple star system with resource-rich planets. Gravitational anomalies reported.',
    aliens: ['klingons', 'ferengi']
  },
  { 
    id: 5, 
    name: 'TRAPPIST-1', 
    type: 'planet',
    distance: 40.66,
    coordinates: { x: 30, y: 55, z: -4 },
    threat: 'MODERATE', 
    resources: 'HIGH',
    services: ['refuel', 'exploration', 'repairs', 'trade', 'weapons'],
    tradeType: 'industrial',
    primaryColor: '#aa44ff',
    secondaryColor: '#cc66ff',
    hasRings: true,
    description: 'Seven-planet system with unusual energy signatures. Three planets in habitable zone.',
    aliens: ['vulcans', 'andorians', 'aquatic']
  },
  { 
    id: 14, 
    name: 'WAYPOINT STATION ALPHA', 
    type: 'station',
    distance: 200,
    coordinates: { x: 55, y: 35, z: 8 },
    threat: 'MINIMAL', 
    resources: 'FUEL DEPOT',
    services: ['refuel', 'repairs', 'trade', 'weapons', 'modules', 'crew'],
    tradeType: 'default',
    primaryColor: '#00ffff',
    secondaryColor: '#00ffff',
    hasRings: false,
    description: 'First waypoint station for deep space travelers. Safe harbor in the void.'
  },
  { 
    id: 15, 
    name: 'DEEP SPACE STATION MIDWAY', 
    type: 'station',
    distance: 350,
    coordinates: { x: 65, y: 45, z: -6 },
    threat: 'MINIMAL', 
    resources: 'FUEL DEPOT',
    services: ['refuel', 'repairs', 'upgrades', 'trade', 'weapons', 'crew'],
    tradeType: 'default',
    primaryColor: '#00ffff',
    secondaryColor: '#00ffff',
    hasRings: false,
    description: 'Critical refueling hub at the edge of explored space. Last safe harbor before the frontier.'
  },
  { 
    id: 16, 
    name: 'POLARIS', 
    type: 'planet',
    distance: 433,
    coordinates: { x: 25, y: 60, z: 10 },
    threat: 'LOW', 
    resources: 'HIGH',
    services: ['refuel', 'exploration', 'repairs', 'trade', 'weapons'],
    tradeType: 'industrial',
    primaryColor: '#ffffaa',
    secondaryColor: '#ffffcc',
    hasRings: false,
    description: 'The North Star. Yellow supergiant in triple star system. Navigation reference point.'
  },
  { 
    id: 17, 
    name: 'TRADING POST NEXUS', 
    type: 'station',
    distance: 550,
    coordinates: { x: 35, y: 40, z: -8 },
    threat: 'LOW', 
    resources: 'FUEL DEPOT',
    services: ['refuel', 'repairs', 'upgrades', 'weapons', 'trade', 'modules'],
    tradeType: 'station_advanced',
    primaryColor: '#00ffff',
    secondaryColor: '#00ffff',
    hasRings: false,
    description: 'Bustling trade hub at the crossroads of deep space routes. Premium weapons and upgrades.'
  },
  { 
    id: 18, 
    name: 'BETELGEUSE', 
    type: 'planet',
    distance: 650,
    coordinates: { x: 58, y: 65, z: 12 },
    threat: 'MODERATE', 
    resources: 'VERY HIGH',
    services: ['refuel', 'exploration', 'repairs', 'trade'],
    tradeType: 'remote',
    primaryColor: '#ff6644',
    secondaryColor: '#ff8866',
    hasRings: false,
    description: 'Red supergiant nearing supernova. Extreme radiation. Rare heavy elements abundant.',
    aliens: ['crystalline']
  },
  { 
    id: 20, 
    name: 'RIGEL', 
    type: 'planet',
    distance: 863,
    coordinates: { x: 68, y: 38, z: -10 },
    threat: 'HIGH', 
    resources: 'VERY HIGH',
    services: ['refuel', 'exploration', 'repairs', 'trade'],
    tradeType: 'remote',
    primaryColor: '#aaccff',
    secondaryColor: '#ccddff',
    hasRings: false,
    description: 'Blue supergiant 120,000 times brighter than Sol. Exotic matter deposits worth the danger.',
    aliens: ['crystalline', 'greys']
  },
  { 
    id: 21, 
    name: 'VEGA', 
    type: 'planet',
    distance: 25,
    coordinates: { x: 52, y: 42, z: 4 },
    threat: 'LOW', 
    resources: 'HIGH',
    services: ['refuel', 'exploration', 'repairs', 'trade'],
    tradeType: 'industrial',
    primaryColor: '#eeeeff',
    secondaryColor: '#ffffff',
    hasRings: true,
    description: 'Brilliant white star with massive debris disk. Rich in raw materials.',
    aliens: ['ferengi']
  },
  { 
    id: 36, 
    name: 'SIRIUS', 
    type: 'planet',
    distance: 8.6,
    coordinates: { x: 38, y: 58, z: -3 },
    threat: 'MINIMAL', 
    resources: 'HIGH',
    services: ['refuel', 'exploration', 'repairs', 'trade', 'weapons'],
    tradeType: 'industrial',
    primaryColor: '#ffffff',
    secondaryColor: '#eeeeff',
    hasRings: false,
    description: 'Brightest star in Earth\'s sky. Binary system with white dwarf. Major technology hub.',
    aliens: ['vulcans', 'andorians', 'ferengi']
  },
  { 
    id: 99, 
    name: 'SAGITTARIUS A*', 
    type: 'blackhole',
    distance: 26000,
    coordinates: { x: 48, y: 48, z: -15 },
    threat: 'EXTREME', 
    resources: 'GRAVITATIONAL ANOMALY',
    services: [],
    tradeType: 'none',
    primaryColor: '#000000',
    secondaryColor: '#ff00ff',
    hasRings: true,
    description: 'Supermassive black hole at the galactic center. Warps spacetime itself.'
  },
  { 
    id: 40, 
    name: 'THE VOID', 
    type: 'station',
    distance: 1500,
    coordinates: { x: 70, y: 50, z: 15 },
    threat: 'EXTREME', 
    resources: 'FUEL DEPOT',
    services: ['refuel', 'repairs', 'weapons', 'upgrades', 'trade', 'modules'],
    tradeType: 'station_advanced',
    primaryColor: '#ff00ff',
    secondaryColor: '#ff00ff',
    hasRings: false,
    description: 'Mysterious station at the edge of known space. Unknown origin. Legendary weapons rumored.'
  }
];

// Star positions for map visualization
export const starPositions = starSystems.reduce((acc, system) => {
  acc[system.id] = system.coordinates;
  return acc;
}, {});
